import { useLocale } from '@repo/i18n/react';
import { useEffect, useRef, useState } from 'react';
import { cn } from '../../lib/utils';
import { Input } from './input';
import { Textarea } from './textarea';

interface TypewriterFieldProps extends React.ComponentProps<'input'> {
  speed?: number;
  initialDelay?: number;
}

export function TypewriterField({
  value,
  speed = 150,
  initialDelay = 800,
  className,
  onFocus,
  onChange,
  ...props
}: TypewriterFieldProps) {
  useLocale();

  const targetValue = String(value || '');
  const [displayedValue, setDisplayedValue] = useState('');
  const [isAnimationDone, setIsAnimationDone] = useState(false);

  // State: Has the initial delay passed?
  const [hasStarted, setHasStarted] = useState(false);

  // State: Has user interacted with the field?
  const [hasUserInteracted, setHasUserInteracted] = useState(false);

  // Track which character index should be animated
  const [animatingCharIndex, setAnimatingCharIndex] = useState<number | null>(null);

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 1. Reset form or value change management
  useEffect(() => {
    // If value is empty
    if (!targetValue) {
      const timeout = setTimeout(() => {
        setDisplayedValue('');
        setHasStarted(false);
        setHasUserInteracted(false);
        setAnimatingCharIndex(null);
      }, 0);
      return () => clearTimeout(timeout);
    }

    // If animation was done but new data arrived from parent
    if (targetValue !== displayedValue && isAnimationDone) {
      const timeout = setTimeout(() => {
        if (hasUserInteracted) {
          // User already edited this field: stay in normal input mode, just show new value
          setDisplayedValue(targetValue);
          setIsAnimationDone(true);
        } else {
          // Reinitialize for fresh typewriter animation (e.g. autofill)
          setIsAnimationDone(false);
          setHasStarted(false);
          setDisplayedValue('');
          setAnimatingCharIndex(null);
        }
      }, 0);
      return () => clearTimeout(timeout);
    }
  }, [targetValue, displayedValue, isAnimationDone, hasUserInteracted]);

  // 2. Initial delay management
  useEffect(() => {
    // If not started yet, animation not done, and we have value to type
    if (!hasStarted && !isAnimationDone && targetValue) {
      const timer = setTimeout(() => {
        setHasStarted(true);
      }, initialDelay);
      return () => clearTimeout(timer);
    }
  }, [hasStarted, isAnimationDone, targetValue, initialDelay]);

  // 3. Typing logic (main)
  useEffect(() => {
    // Don't type until initial delay is over (hasStarted is false)
    if (!hasStarted) return;

    if (isAnimationDone || displayedValue === targetValue) {
      const timeout = setTimeout(() => {
        setIsAnimationDone(true);
      }, 0);
      return () => clearTimeout(timeout);
    }

    const nextCharIndex = displayedValue.length;
    if (nextCharIndex < targetValue.length) {
      timeoutRef.current = setTimeout(() => {
        setDisplayedValue(targetValue.slice(0, nextCharIndex + 1));
        // Trigger animation for the new character
        setAnimatingCharIndex(nextCharIndex);
        // Clear animation state after animation completes
        setTimeout(() => {
          setAnimatingCharIndex(null);
        }, 300);
      }, speed);
    } else {
      const timeout = setTimeout(() => {
        setIsAnimationDone(true);
      }, 0);
      return () => clearTimeout(timeout);
    }

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [displayedValue, targetValue, isAnimationDone, speed, hasStarted]);

  // 4. Handle focus (stop animation and delay)
  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setHasUserInteracted(true);
    setAnimatingCharIndex(null);
    if (!isAnimationDone) {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      // Even if we're in the 3-second delay, show everything on user focus
      setHasStarted(true);
      setDisplayedValue(targetValue);
      setIsAnimationDone(true);
    }
    if (onFocus) onFocus(e);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setHasUserInteracted(true);
    setIsAnimationDone(true);
    setHasStarted(true);
    setDisplayedValue(e.target.value);
    setAnimatingCharIndex(null);
    if (onChange) onChange(e);
  };

  // Determine if we should show auto-typing styles
  // Styles remain until user interacts, even after animation completes
  const isAutoTyping = !hasUserInteracted && displayedValue.length > 0;

  // Render characters with animation
  const renderAnimatedText = () => {
    if (hasUserInteracted || !isAutoTyping) {
      return null; // Don't render animated text if user interacted
    }

    // After animation is done, keep showing the overlay but without animation
    if (isAnimationDone) {
      return (
        <div
          aria-hidden="true"
          className={cn(
            'pointer-events-none absolute inset-0 flex items-center overflow-hidden px-2.5',
            'font-bold text-base text-primary md:text-sm',
            'h-9 rounded-md',
          )}
        >
          <span className="block min-w-0 truncate">
            {displayedValue.split('').map((char, index) => (
              <span key={`${index}-${char}-${displayedValue.length}`}>
                {char === ' ' ? '\u00A0' : char}
              </span>
            ))}
          </span>
        </div>
      );
    }

    return (
      <div
        aria-hidden="true"
        className={cn(
          'pointer-events-none absolute inset-0 flex items-center overflow-hidden px-2.5',
          'font-bold text-base text-primary md:text-sm',
          'h-9 rounded-md',
        )}
      >
        <span className="block min-w-0 truncate">
          {displayedValue.split('').map((char, index) => (
            <span
              className={cn(index === animatingCharIndex && 'char-zoom-in')}
              key={`${index}-${char}-${displayedValue.length}`}
            >
              {char === ' ' ? '\u00A0' : char}
            </span>
          ))}
        </span>
      </div>
    );
  };

  return (
    <div className="relative min-w-0 overflow-hidden">
      <Input
        {...props}
        className={cn(className, isAutoTyping && 'text-transparent')}
        onChange={handleChange}
        onFocus={handleFocus}
        style={{
          caretColor: isAutoTyping ? 'transparent' : undefined,
        }}
        value={isAnimationDone && value ? value : displayedValue}
      />
      {renderAnimatedText()}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* AutofillTextarea – same blue/primary autofill styling as TypewriterField */
/* ------------------------------------------------------------------ */

interface AutofillTextareaProps extends React.ComponentProps<'textarea'> {
  value?: string;
}

export function AutofillTextarea({
  value,
  className,
  onFocus,
  onChange,
  ...props
}: AutofillTextareaProps) {
  useLocale();

  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const hasAutofillValue = Boolean(value && value?.trim?.()?.length > 0);
  const isAutofilled = hasAutofillValue && !hasUserInteracted;

  const handleFocus = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    setHasUserInteracted(true);
    onFocus?.(e);
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setHasUserInteracted(true);
    onChange?.(e);
  };

  return (
    <Textarea
      {...props}
      className={cn(
        isAutofilled &&
          'border-primary/30 bg-primary/5 font-bold text-primary focus-visible:border-primary focus-visible:ring-primary/30',
        className,
      )}
      onChange={handleChange}
      onFocus={handleFocus}
      value={value}
    />
  );
}
